import React from 'react';
import styled from '@emotion/styled';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { filterTokens } from './filterTokens';

const { color } = filterTokens;

const OptionButton = styled.button<{ selected: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    height: ${filterTokens.optionHeight}px;
    padding: 0 15px;
    border-radius: ${filterTokens.optionHeight / 2}px;
    border: 1px solid ${({ selected }) => (selected ? color.accent : color.border)};
    background: ${({ selected }) => (selected ? color.selectedBg : color.surface)};
    color: ${({ selected }) => (selected ? color.selectedText : color.text)};
    font-family: inherit;
    font-size: 14px;
    font-weight: ${({ selected }) => (selected ? 700 : 500)};
    white-space: nowrap;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;

    &:focus-visible {
        outline: 2px solid ${color.accent};
        outline-offset: 2px;
    }

    svg {
        font-size: 16px;
    }
`;

export interface OptionChipProps {
    selected: boolean;
    onClick: () => void;
    children: React.ReactNode;
    /** 글만으로 뜻이 모자랄 때(예: "10/14" → "10월 14일 (수) 빼기") */
    ariaLabel?: string;
}

/** 시트·팝업 안의 고르기 칩 — 고르면 체크 표시 */
export const OptionChip = ({ selected, onClick, children, ariaLabel }: OptionChipProps) => (
    <OptionButton type="button" selected={selected} aria-pressed={selected} aria-label={ariaLabel} onClick={onClick}>
        {selected && <CheckRoundedIcon aria-hidden="true" />}
        {children}
    </OptionButton>
);

const Group = styled.div`
    & + & {
        margin-top: 4px;
    }
`;

const GroupLabel = styled.div<{ strong: boolean }>`
    margin: ${({ strong }) => (strong ? '18px 0 10px' : '14px 0 8px')};
    font-size: ${({ strong }) => (strong ? '16px' : '13px')};
    font-weight: ${({ strong }) => (strong ? 800 : 600)};
    color: ${({ strong }) => (strong ? color.text : color.textMuted)};
`;

const Hint = styled.div`
    margin: -4px 0 8px;
    font-size: 12px;
    color: ${color.textMuted};
`;

const Options = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;

export interface OptionGroupProps {
    /** 그룹 이름(임상·경영…). 없으면 칩만 */
    label?: string;
    /** 큰 제목(한 시트에 여러 조건을 모을 때의 조건 이름) */
    strong?: boolean;
    hint?: string;
    children: React.ReactNode;
}

/** 이름 + 칩 여러 개(줄바꿈) */
export const OptionGroup = ({ label, strong = false, hint, children }: OptionGroupProps) => (
    <Group role="group" aria-label={label}>
        {label && <GroupLabel strong={strong}>{label}</GroupLabel>}
        {hint && <Hint>{hint}</Hint>}
        <Options>{children}</Options>
    </Group>
);

/** 조건 이름만(칩 없이 그룹들을 묶을 때) */
export const OptionSectionTitle = styled.div`
    margin: 18px 0 2px;
    font-size: 16px;
    font-weight: 800;
    color: ${color.text};
`;
